import { Link, useParams } from '@tanstack/react-router'
import { mdiPlus } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { useCan } from '../../contexts/PermissionsContext'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCampaigns, type CampaignSummary } from '../../hooks/useCampaigns'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { EmptyState } from '../ui/empty-state'
import { AppIcon } from '../ui/icon'
import { Progress } from '../ui/progress'
import { Skeleton } from '../ui/skeleton'
import { CAMPAIGN_STATUS } from './campaign-status'
import { StatusPill } from './StatusPill'

export function formatDate(iso: string | null | undefined): string {
    if (!iso) return ''
    const [y, m, d] = iso.slice(0, 10).split('-')
    return `${d}/${m}/${y}`
}

/**
 * The campaigns (spec 2026-10-20-12): every campaign for a manager, the ones
 * they are an agent of for anybody else.
 */
export function CampaignsPage() {
    const { org } = useParams({ from: '/$org/campaigns/' })
    const { t } = useTranslation('campaigns')
    useBreadcrumbs([{ title: t('common:nav.home'), path: `/${org}` }, { title: t('title') }])
    // A narrowed write_assigned fills, it never creates.
    const canManage = useCan('campaigns.write', undefined, { exact: true })
    const { data: campaigns, isLoading } = useCampaigns(org)

    return (
        <div className="space-y-6" data-testid="campaigns-page">
            <PageHeader
                icon={SECTION_ICONS.campaigns}
                title={t('title')}
                description={t('description')}
                actions={
                    canManage ? (
                        <Button asChild aria-label={t('new')} title={t('new')} data-testid="campaigns-new-btn">
                            <Link to="/$org/campaigns/new" params={{ org }}>
                                <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                                <span className="hidden md:inline">{t('new')}</span>
                            </Link>
                        </Button>
                    ) : undefined
                }
            />
            {isLoading ? (
                <Skeleton className="h-32 w-full" />
            ) : !campaigns || campaigns.length === 0 ? (
                <EmptyState
                    icon={SECTION_ICONS.campaigns}
                    title={t('empty_title')}
                    description={canManage ? t('empty_description_manager') : t('empty_description_agent')}
                    data-testid="campaigns-empty"
                />
            ) : (
                <ul className="divide-y rounded-lg border" data-testid="campaigns-list">
                    {campaigns.map((c) => (
                        <CampaignRow key={c.uid} org={org} campaign={c} />
                    ))}
                </ul>
            )}
        </div>
    )
}

function CampaignRow({ org, campaign }: { org: string; campaign: CampaignSummary }) {
    const { t } = useTranslation('campaigns')
    const done = campaign.units.total ? Math.round((campaign.units.done / campaign.units.total) * 100) : 0
    return (
        <li data-testid={`campaigns-row-${campaign.uid}`}>
            <Link
                to="/$org/campaigns/$campaignUid"
                params={{ org, campaignUid: campaign.uid }}
                search={{ tab: undefined }}
                className="flex flex-col gap-2 p-4 hover:bg-muted/50 md:flex-row md:items-center md:gap-4"
                data-testid={`campaigns-link-${campaign.uid}`}
            >
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium">{campaign.name}</span>
                        <StatusPill map={CAMPAIGN_STATUS} family="status" value={campaign.status} />
                        {campaign.is_agent && <Badge variant="info">{t('you_are_agent')}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {t(`level.${campaign.level}`)}
                        {campaign.due_on && ` · ${t('due_on', { date: formatDate(campaign.due_on) })}`}
                        {` · ${t('agents_count', { count: campaign.agents })}`}
                    </p>
                </div>
                {campaign.status !== 'draft' && (
                    <div className="w-full md:w-56">
                        <Progress value={done} className="h-2" />
                        <p className="mt-1 text-xs text-muted-foreground">
                            {t('units_done', { done: campaign.units.done, total: campaign.units.total })}
                        </p>
                    </div>
                )}
            </Link>
        </li>
    )
}
